import { useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { supabase } from "../../lib/supabase";

// 言語選択肢。key は安定した識別子（i18n キー）、label は表示／保存に使う訳語。
// 選択値は従来どおり「表示ラベル」を locale 列へ保存する（表示＝保存の一貫性を維持）。
const LANGUAGE_KEYS = [
  "japanese", "english", "chineseMandarin", "chineseCantonese", "korean",
  "spanish", "french", "german", "portuguese", "italian",
  "russian", "arabic", "hindi", "turkish", "other",
] as const;

const CATEGORY_KEYS = ["tutor", "aspiring", "sla"] as const;

type Status = "idle" | "submitting" | "success" | "error";

export default function RegisterPage() {
  const { t } = useTranslation("register");
  const LANGUAGES = LANGUAGE_KEYS.map((k) => t(`languages.${k}`));
  const CATEGORIES = CATEGORY_KEYS.map((value) => ({
    value,
    label: t(`categories.${value}`),
  }));
  const [status, setStatus] = useState<Status>("idle");
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [langInput, setLangInput] = useState("");
  const [consentPublish, setConsentPublish] = useState(false);
  // 失敗時にトーストへ出す簡易メッセージ（成功/失敗の分岐は status で行う）。
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const toggleLanguage = (lang: string) => {
    setSelectedLanguages((prev) =>
      prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang]
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg(null);

    const form = e.currentTarget;
    const val = (name: string) =>
      ((form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null)?.value ?? "").trim();

    const name = val("name");
    const birthdate = val("birthdate");
    const email = val("email");
    const nationality = val("nationality");
    const organizationName = val("organization_name");
    const category = val("category");
    const categoryLabel = CATEGORIES.find((c) => c.value === category)?.label ?? category;
    const languages = selectedLanguages.join("、");

    // 既存の細かいフォーム項目（生年月日・国籍・区分・扱える言語）は
    // community_signups の構造化列に無いため message に要約して保持する。
    // locale には扱える言語、organization_name は専用列へ入れる。
    const message = [
      `生年月日: ${birthdate}`,
      `国籍: ${nationality}`,
      `区分: ${categoryLabel}`,
      `扱える言語: ${languages || "（未選択）"}`,
    ].join("\n");

    try {
      // 1) 自社管理の community_signups へ保存（Readdy 外部送信を置換）。
      //    status は RLS の WITH CHECK 上 'new' 固定（デフォルト）。掲載同意は consent_publish。
      const { error: insertError } = await supabase.from("community_signups").insert({
        email,
        name: name || null,
        locale: languages || null,
        organization_name: organizationName || null,
        message,
        consent_publish: consentPublish,
      });

      if (insertError) {
        setErrorMsg(t("errors.saveFailed"));
        setStatus("error");
        return;
      }

      // 2) 受領自動返信メール（送信者本人宛）。
      //    メール送信に失敗しても登録自体は成功しているため success のまま続行する
      //    （ブロッキングしない・ベストエフォート）。
      const { error: fnError } = await supabase.functions.invoke("send-signup-ack", {
        body: { email, name: name || undefined },
      });
      if (fnError) {
        // 保存は成功・確認メールのみ失敗。コンソールに残し UI は成功扱い。
        console.warn("send-signup-ack failed (registration saved):", fnError.message);
      }

      setStatus("success");
    } catch (err) {
      console.error("community signup failed:", err);
      setErrorMsg(t("errors.submitFailed"));
      setStatus("error");
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    height: 48,
    padding: "0 16px",
    borderRadius: 8,
    background: "#FBF5E8",
    border: "1.5px solid transparent",
    fontFamily: "'Noto Sans JP', sans-serif",
    fontSize: 14,
    color: "#1A1A1A",
    outline: "none",
    transition: "border-color 0.2s, background 0.2s",
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: "'Noto Sans JP', sans-serif",
    fontWeight: 600,
    fontSize: 13,
    color: "#1A1A1A",
    marginBottom: 8,
    display: "block",
  };

  return (
    <main style={{ background: "#FAF5E8", minHeight: "100vh" }}>
      <Navbar />

      {/* Page Header */}
      <section
        style={{
          paddingTop: 140,
          paddingBottom: 60,
          textAlign: "center",
        }}
      >
        <div className="max-w-2xl mx-auto px-6">
          <span
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.14em",
              color: "#2C5F4F",
              textTransform: "uppercase",
            }}
          >
            {t("label")}
          </span>
          <h1
            style={{
              fontFamily: "'Noto Sans JP', sans-serif",
              fontWeight: 800,
              fontSize: "clamp(28px, 3.5vw, 40px)",
              color: "#1A1A1A",
              lineHeight: 1.4,
              marginTop: 12,
              marginBottom: 16,
            }}
          >
            {t("heading")}
          </h1>
          <p
            style={{
              fontFamily: "'Noto Serif JP', serif",
              fontSize: 15,
              color: "#666666",
              lineHeight: 1.9,
            }}
          >
            {t("intro")}
          </p>
        </div>
      </section>

      {/* Form */}
      <section style={{ paddingBottom: 100 }}>
        <div className="max-w-xl mx-auto px-6">
          {status === "success" ? (
            <div
              style={{
                background: "#FAF5E8",
                borderRadius: 24,
                padding: "64px 48px",
                textAlign: "center",
                border: "1px solid rgba(180,150,90,0.2)",
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "rgba(44,95,79,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 24px",
                }}
              >
                <i className="ri-check-line" style={{ fontSize: 28, color: "#2C5F4F" }} />
              </div>
              <h2
                style={{
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontWeight: 700,
                  fontSize: 22,
                  color: "#1A1A1A",
                  marginBottom: 12,
                }}
              >
                {t("successTitle")}
              </h2>
              <p
                style={{
                  fontFamily: "'Noto Serif JP', serif",
                  fontSize: 14,
                  color: "#666666",
                  lineHeight: 1.9,
                }}
              >
                {t("successBody1")}
                <br />
                {t("successBody2")}
              </p>
            </div>
          ) : (
            <form
              id="eachteach-community-registration"
              onSubmit={handleSubmit}
              style={{
                background: "#FAF5E8",
                borderRadius: 24,
                padding: "52px 48px",
                border: "1px solid rgba(180,150,90,0.2)",
              }}
            >
              {/* 氏名 */}
              <div style={{ marginBottom: 28 }}>
                <label htmlFor="name" style={labelStyle}>
                  {t("form.name")} <span style={{ color: "#2C5F4F" }}>{t("form.requiredMark")}</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder={t("form.namePlaceholder")}
                  style={inputStyle}
                  onFocus={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = "#2C5F4F";
                    (e.target as HTMLInputElement).style.background = "#ffffff";
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = "transparent";
                    (e.target as HTMLInputElement).style.background = "#FBF5E8";
                  }}
                />
              </div>

              {/* 生年月日 */}
              <div style={{ marginBottom: 28 }}>
                <label htmlFor="birthdate" style={labelStyle}>
                  {t("form.birthdate")} <span style={{ color: "#2C5F4F" }}>{t("form.requiredMark")}</span>
                </label>
                <input
                  id="birthdate"
                  name="birthdate"
                  type="date"
                  required
                  style={inputStyle}
                  onFocus={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = "#2C5F4F";
                    (e.target as HTMLInputElement).style.background = "#ffffff";
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = "transparent";
                    (e.target as HTMLInputElement).style.background = "#FBF5E8";
                  }}
                />
              </div>

              {/* メールアドレス */}
              <div style={{ marginBottom: 28 }}>
                <label htmlFor="email" style={labelStyle}>
                  {t("form.email")} <span style={{ color: "#2C5F4F" }}>{t("form.requiredMark")}</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder={t("form.emailPlaceholder")}
                  style={inputStyle}
                  onFocus={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = "#2C5F4F";
                    (e.target as HTMLInputElement).style.background = "#ffffff";
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = "transparent";
                    (e.target as HTMLInputElement).style.background = "#FBF5E8";
                  }}
                />
              </div>

              {/* 国籍 */}
              <div style={{ marginBottom: 28 }}>
                <label htmlFor="nationality" style={labelStyle}>
                  {t("form.nationality")} <span style={{ color: "#2C5F4F" }}>{t("form.requiredMark")}</span>
                </label>
                <input
                  id="nationality"
                  name="nationality"
                  type="text"
                  required
                  placeholder={t("form.nationalityPlaceholder")}
                  style={inputStyle}
                  onFocus={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = "#2C5F4F";
                    (e.target as HTMLInputElement).style.background = "#ffffff";
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = "transparent";
                    (e.target as HTMLInputElement).style.background = "#FBF5E8";
                  }}
                />
              </div>

              {/* 団体名（任意） */}
              <div style={{ marginBottom: 28 }}>
                <label htmlFor="organization_name" style={labelStyle}>
                  {t("form.organization")}
                </label>
                <input
                  id="organization_name"
                  name="organization_name"
                  type="text"
                  placeholder={t("form.organizationPlaceholder")}
                  style={inputStyle}
                  onFocus={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = "#2C5F4F";
                    (e.target as HTMLInputElement).style.background = "#ffffff";
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = "transparent";
                    (e.target as HTMLInputElement).style.background = "#FBF5E8";
                  }}
                />
              </div>

              {/* 扱える言語 */}
              <div style={{ marginBottom: 28 }}>
                <label style={labelStyle}>
                  {t("form.languages")} <span style={{ color: "#2C5F4F" }}>{t("form.requiredMark")}</span>
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {LANGUAGES.map((lang) => {
                    const selected = selectedLanguages.includes(lang);
                    return (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => toggleLanguage(lang)}
                        className="whitespace-nowrap cursor-pointer"
                        style={{
                          padding: "6px 14px",
                          borderRadius: 999,
                          border: selected
                            ? "1.5px solid #2C5F4F"
                            : "1.5px solid rgba(44,95,79,0.2)",
                          background: selected ? "rgba(44,95,79,0.08)" : "transparent",
                          fontFamily: "'Noto Sans JP', sans-serif",
                          fontSize: 12,
                          fontWeight: selected ? 600 : 400,
                          color: selected ? "#2C5F4F" : "#666666",
                          transition: "all 0.15s",
                        }}
                      >
                        {lang}
                      </button>
                    );
                  })}
                </div>
                {/* Custom language input */}
                <input
                  type="text"
                  placeholder={t("form.languagesOtherPlaceholder")}
                  value={langInput}
                  onChange={(e) => setLangInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && langInput.trim()) {
                      e.preventDefault();
                      toggleLanguage(langInput.trim());
                      setLangInput("");
                    }
                  }}
                  style={{ ...inputStyle, marginTop: 4 }}
                  onFocus={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = "#2C5F4F";
                    (e.target as HTMLInputElement).style.background = "#ffffff";
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = "transparent";
                    (e.target as HTMLInputElement).style.background = "#FBF5E8";
                  }}
                />
                {selectedLanguages.length > 0 && (
                  <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: 12, color: "#2C5F4F", marginTop: 6 }}>
                    {t("form.selected")}: {selectedLanguages.join("、")}
                  </p>
                )}
              </div>

              {/* 区分 */}
              <div style={{ marginBottom: 40 }}>
                <label htmlFor="category" style={labelStyle}>
                  {t("form.category")} <span style={{ color: "#2C5F4F" }}>{t("form.requiredMark")}</span>
                </label>
                <div style={{ position: "relative" }}>
                  <select
                    id="category"
                    name="category"
                    required
                    style={{
                      ...inputStyle,
                      appearance: "none",
                      paddingRight: 40,
                      cursor: "pointer",
                    }}
                    onFocus={(e) => {
                      (e.target as HTMLSelectElement).style.borderColor = "#2C5F4F";
                      (e.target as HTMLSelectElement).style.background = "#ffffff";
                    }}
                    onBlur={(e) => {
                      (e.target as HTMLSelectElement).style.borderColor = "transparent";
                      (e.target as HTMLSelectElement).style.background = "#FBF5E8";
                    }}
                  >
                    <option value="">{t("form.categorySelect")}</option>
                    {CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                  <div
                    className="flex items-center justify-center w-5 h-5"
                    style={{
                      position: "absolute",
                      right: 14,
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                      color: "#2C5F4F",
                    }}
                  >
                    <i className="ri-arrow-down-s-line" style={{ fontSize: 18 }} />
                  </div>
                </div>
              </div>

              {/* 掲載同意（団体名を LP に掲載する場合の事前同意） */}
              {/*
                掲載は consent_publish=true かつ admin 承認済みの行のみ（migration 参照）。
                同意は遡及取得できないため、登録時点で先行収集する。
              */}
              <div style={{ marginBottom: 32 }}>
                <label
                  htmlFor="consent_publish"
                  className="cursor-pointer"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: "14px 16px",
                    borderRadius: 12,
                    background: "rgba(44,95,79,0.04)",
                    border: "1px solid rgba(44,95,79,0.12)",
                  }}
                >
                  <input
                    id="consent_publish"
                    name="consent_publish"
                    type="checkbox"
                    checked={consentPublish}
                    onChange={(e) => setConsentPublish(e.target.checked)}
                    style={{
                      width: 18,
                      height: 18,
                      marginTop: 2,
                      accentColor: "#2C5F4F",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontFamily: "'Noto Sans JP', sans-serif",
                      fontSize: 13,
                      color: "#444444",
                      lineHeight: 1.7,
                    }}
                  >
                    {t("form.consent")}
                    <br />
                    <span style={{ color: "#888888", fontSize: 12 }}>
                      {t("form.consentNote")}
                    </span>
                  </span>
                </label>
              </div>

              {/* Error */}
              {status === "error" && (
                <div
                  style={{
                    padding: "12px 16px",
                    borderRadius: 8,
                    background: "rgba(220,53,69,0.08)",
                    border: "1px solid rgba(220,53,69,0.2)",
                    fontFamily: "'Noto Sans JP', sans-serif",
                    fontSize: 13,
                    color: "#c0392b",
                    marginBottom: 20,
                  }}
                >
                  {errorMsg ?? t("errors.submitFailed")}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full whitespace-nowrap cursor-pointer"
                style={{
                  height: 52,
                  borderRadius: 12,
                  background: status === "submitting" ? "#7aaa98" : "#2C5F4F",
                  color: "#ffffff",
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontWeight: 700,
                  fontSize: 15,
                  border: "none",
                  cursor: status === "submitting" ? "not-allowed" : "pointer",
                  transition: "background 0.2s, transform 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
                onMouseEnter={(e) => {
                  if (status !== "submitting") {
                    (e.currentTarget as HTMLButtonElement).style.background = "#3A7A63";
                  }
                }}
                onMouseLeave={(e) => {
                  if (status !== "submitting") {
                    (e.currentTarget as HTMLButtonElement).style.background = "#2C5F4F";
                  }
                }}
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line" style={{ animation: "spin 1s linear infinite" }} />
                    {t("form.submitting")}
                  </>
                ) : (
                  <>
                    {t("form.submit")}
                    <i className="ri-arrow-right-line" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
